import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { useBooking } from "@/context/BookingContext";
import { SITE, LOCATIONS, VEHICLES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service_type: "",
  trip_type: "one_way",
  origin: "",
  destination: "",
  date: "",
  return_date: "",
  time: "",
  adults: "1",
  children: "0",
  flight_number: "",
  notes: "",
};

export const Reservation = () => {
  const { t, lang } = useLang();
  const { prefill, vehicle, setVehicle } = useBooking();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    setForm((prev) => ({
      ...prev,
      trip_type: prefill.trip_type ?? prev.trip_type,
      origin: prefill.origin || prev.origin,
      destination: prefill.destination || prev.destination,
      date: prefill.date || prev.date,
      return_date: prefill.return_date || prev.return_date,
      adults: String(prefill.adults ?? prev.adults),
      children: String(prefill.children ?? prev.children),
    }));
  }, [prefill]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const buildWhatsApp = () => {
    const r = t.reservation;
    const pax = `${form.adults} ${r.adults}${parseInt(form.children, 10) > 0 ? `, ${form.children} ${r.children}` : ""}`;
    const lines = [
      `*${SITE.brand} - ${r.title}*`,
      `${r.name}: ${form.name}`,
      `${r.phone}: ${form.phone}`,
      form.email ? `${r.email}: ${form.email}` : null,
      `${r.service}: ${form.service_type}`,
      vehicle ? `${r.vehicle}: ${vehicle}` : null,
      `${r.tripType}: ${form.trip_type === "round_trip" ? r.roundTrip : r.oneWay}`,
      `${r.origin}: ${form.origin}`,
      `${r.destination}: ${form.destination}`,
      `${r.date}: ${form.date}${form.time ? " " + form.time : ""}`,
      form.trip_type === "round_trip" && form.return_date ? `${r.returnDate}: ${form.return_date}` : null,
      `${r.passengers}: ${pax}`,
      form.flight_number ? `${r.flight}: ${form.flight_number}` : null,
      form.notes ? `${r.notes}: ${form.notes}` : null,
    ].filter(Boolean);
    return `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_type || !form.origin || !form.destination || !form.date) {
      toast.error(t.reservation.required);
      return;
    }
    setLoading(true);
    try {
      const adults = parseInt(form.adults, 10) || 1;
      const children = parseInt(form.children, 10) || 0;
      await axios.post(`${API}/reservations`, {
        ...form,
        vehicle,
        adults,
        children,
        passengers: adults + children,
      });
      toast.success(t.reservation.success);
      const url = buildWhatsApp();
      window.open(url, "_blank");
      setForm(initial);
      setVehicle("");
    } catch (err) {
      console.error(err);
      toast.error(t.reservation.error);
    } finally {
      setLoading(false);
    }
  };

  const r = t.reservation;
  const labelCls = "font-body text-xs uppercase tracking-wider text-jungle-light/70 mb-1.5 block";
  const fieldCls = "bg-white border-border rounded-none focus:border-teal focus-visible:ring-0 font-body text-jungle";

  return (
    <section id="book" className="py-20 md:py-32 bg-jungle relative overflow-hidden" data-testid="reservation-section">
      <img
        src="https://images.unsplash.com/photo-1565358720137-55235e0878a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxjYW5jdW4lMjB0dXJxdW9pc2UlMjBvY2VhbiUyMGJlYWNofGVufDB8fHx8MTc4MTcyNjgxNHww&ixlib=rb-4.1.0&q=85"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-10"
      />
      <div className="relative px-6 md:px-12 lg:px-20 mx-auto max-w-5xl">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-teal font-bold mb-4">{r.overline}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-sand mb-4">{r.title}</h2>
          <p className="font-body text-sand/70">{r.subtitle}</p>
        </div>

        <form onSubmit={submit} className="bg-sand p-6 md:p-10 border border-border" data-testid="reservation-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{r.name} *</label>
              <Input data-testid="res-name" value={form.name} onChange={set("name")} className={fieldCls} placeholder={r.name} />
            </div>
            <div>
              <label className={labelCls}>{r.phone} *</label>
              <Input data-testid="res-phone" value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="+52 ..." />
            </div>
            <div>
              <label className={labelCls}>{r.email}</label>
              <Input data-testid="res-email" type="email" value={form.email} onChange={set("email")} className={fieldCls} placeholder="email@..." />
            </div>
            <div>
              <label className={labelCls}>{r.service} *</label>
              <Select value={form.service_type} onValueChange={set("service_type")}>
                <SelectTrigger data-testid="res-service" className={fieldCls}>
                  <SelectValue placeholder={r.service} />
                </SelectTrigger>
                <SelectContent>
                  {r.serviceOptions.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>{r.vehicle}</label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger data-testid="res-vehicle" className={fieldCls}>
                  <SelectValue placeholder={r.vehicle} />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLES.map((v) => (
                    <SelectItem key={v.id} value={v.name}>{v.name} · {v.capacity}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>{r.tripType}</label>
              <Select value={form.trip_type} onValueChange={set("trip_type")}>
                <SelectTrigger data-testid="res-trip-type" className={fieldCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_way">{r.oneWay}</SelectItem>
                  <SelectItem value="round_trip">{r.roundTrip}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>{r.adults} *</label>
              <Input data-testid="res-adults" type="number" min="1" value={form.adults} onChange={set("adults")} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{r.children}</label>
              <Input data-testid="res-children" type="number" min="0" value={form.children} onChange={set("children")} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{r.origin} *</label>
              <Input list="res-locations" data-testid="res-origin" value={form.origin} onChange={set("origin")} className={fieldCls} placeholder={r.placeholderOrigin} />
            </div>
            <div>
              <label className={labelCls}>{r.destination} *</label>
              <Input list="res-locations" data-testid="res-destination" value={form.destination} onChange={set("destination")} className={fieldCls} placeholder={r.placeholderDest} />
            </div>
            <datalist id="res-locations">
              {LOCATIONS.map((l) => (<option key={l} value={l} />))}
            </datalist>
            <div>
              <label className={labelCls}>{r.date} *</label>
              <Input data-testid="res-date" type="date" value={form.date} onChange={set("date")} className={fieldCls} />
            </div>
            {form.trip_type === "round_trip" && (
              <div>
                <label className={labelCls}>{r.returnDate}</label>
                <Input data-testid="res-return-date" type="date" value={form.return_date} onChange={set("return_date")} className={fieldCls} />
              </div>
            )}
            <div>
              <label className={labelCls}>{r.time}</label>
              <Input data-testid="res-time" type="time" value={form.time} onChange={set("time")} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{r.flight}</label>
              <Input data-testid="res-flight" value={form.flight_number} onChange={set("flight_number")} className={fieldCls} placeholder="AA1234" />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>{r.notes}</label>
              <Textarea data-testid="res-notes" value={form.notes} onChange={set("notes")} className={fieldCls} rows={3} />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="reservation-submit"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-teal text-sand hover:bg-teal-dark font-body py-4 transition-colors disabled:opacity-60"
          >
            <Send size={18} />
            {loading ? r.submitting : r.submit}
          </button>
        </form>
      </div>
    </section>
  );
};
